import { Injectable } from '@angular/core';
import { IBuilding, IBuildingFloor } from '@shared/interfaces/building.interfaces';
import { Subject } from 'rxjs';
import { AuUtilsFunctions } from '@app/shared';
import { ILocation } from '@app/domain/types';

export interface ILocationTreeNode {
	id: string;
	name: string;
	contentType: string;
	selected: boolean;
	isExpanded: boolean;
	children: string[];
	parentId?: string;
	grandparentId?: string;
}

export interface ILocationNode {
	locationId: string;
	name: string;
	locationType: string;
}

export type LocationTreeNodeType = IBuilding | IBuildingFloor | ILocation;

@Injectable()
export class LocationTreeSelectorService {
	flatTreeNodesChanged$ = new Subject<void>();
	triggerToggleSubject$ = new Subject<LocationTreeNodeType>();
	toggleExpand$ = new Subject<LocationTreeNodeType>();
	expanded$ = new Subject<void>();

	private flatTreeNodesMap = new Map<string, ILocationTreeNode>();

	updateFlatTreeNodeMap(locationId: string, locationNode: ILocationTreeNode) {
		this.flatTreeNodesMap.set(locationId, locationNode);
	}

	getNode(locationId: string): ILocationTreeNode {
		return this.flatTreeNodesMap.get(locationId);
	}

	selectNodeAndItsChildren(node: ILocationTreeNode, selected: boolean) {
		if (!node) {
			return;
		}
		this.updateFlatTreeNodeMap(node.id, {
			...node,
			selected,
		});

		node.children.forEach(childId => {
			const node = this.getNode(childId);

			this.updateFlatTreeNodeMap(childId, {
				...node,
				selected,
			});
		});
		this.flatTreeNodesChanged$.next();
	}

	deselectAllNodesExcept(locationId: string) {
		this.flatTreeNodesMap.forEach(node => {
			if (node.id !== locationId) {
				node.selected = false;
			}
		});
		this.flatTreeNodesChanged$.next();
	}

	deselectAllNodes() {
		this.flatTreeNodesMap.forEach(node => {
			node.selected = false;
		});
		this.flatTreeNodesChanged$.next();
	}

	getFilteredLocations(): ILocationNode[] {
		const nodesArray = Array.from(this.flatTreeNodesMap, ([_, value]) => value);

		return nodesArray
			.filter(node => {
				const nodeParent = this.getNode(node.parentId);
				const nodeGrandParent = this.getNode(node.grandparentId);

				if (node.selected && !nodeParent?.selected && !nodeGrandParent?.selected) {
					return true;
				}
			})
			.map(node => {
				return {
					locationId: node.id,
					name: node.name,
					locationType: AuUtilsFunctions.capitalizeText(node.contentType),
				};
			});
	}

	clearFlatTreeNodeMap() {
		this.flatTreeNodesMap.clear();
	}

	triggerToggleCheckbox(location: LocationTreeNodeType) {
		this.triggerToggleSubject$.next(location);
	}

	openLocationChildren(location: LocationTreeNodeType) {
		this.toggleExpand$.next(location);
	}

	expanded() {
		this.expanded$.next();
	}
}
